import { useState } from "react";
import { queryKeys } from "@/lib/queryKeys";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { isAdminUser } from "@chefaiq/shared/lib/admin";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Pencil, Trash2, BarChart3 } from "lucide-react";
import { CreateVoucherDialog } from "@/components/vouchers/CreateVoucherDialog";
import { VoucherStatsDialog } from "@/components/vouchers/VoucherStatsDialog";
import { Badge } from "@/components/ui/badge";

export default function AdminVouchers() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState<any>(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);

  const queryClient = useQueryClient();
  const { user } = useAuth();

  // All hooks must be called unconditionally before any early return
  const { data: vouchers, isLoading } = useQuery<any>({
    queryKey: queryKeys.vouchers.all(),
    queryFn: async () => {
      const { fetchAdminWorkspaceId } = await import('@/lib/adminWorkspaceHelper');
      const adminWorkspaceId = await fetchAdminWorkspaceId();
      if (!adminWorkspaceId) throw new Error('Admin workspace not found');
      return apiRequest('GET', '/api/vouchers', undefined, {
        'x-workspace-id': adminWorkspaceId
      });
    },
    enabled: isAdminUser(user)
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { fetchAdminWorkspaceId } = await import('@/lib/adminWorkspaceHelper');
      const adminWorkspaceId = await fetchAdminWorkspaceId();
      if (!adminWorkspaceId) throw new Error('Admin workspace not found');
      return apiRequest('DELETE', `/api/vouchers/${id}`, undefined, {
        'x-workspace-id': adminWorkspaceId
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.all() });
      showSuccess('Voucher deleted successfully');
    },
    onError: (error: any) => {
      showError(error?.message || 'Failed to delete voucher');
    }
  });

  // Guard — after all hooks
  if (!isAdminUser(user)) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              You don't have permission to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getDiscountDisplay = (voucher: any) => {
    if (voucher.discountType === 'subscription') {
      return `${voucher.subscriptionDuration} days subscription`;
    }
    if (voucher.discountType === 'percentage') {
      return `${voucher.discountValue}% off`;
    }
    return `₱${voucher.discountValue} off`;
  };

  const getStatusBadge = (voucher: any) => {
    const now = new Date();
    const validFrom = new Date(voucher.validFrom);
    const validUntil = new Date(voucher.validUntil);

    if (!voucher.isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (now < validFrom) {
      return <Badge variant="outline">Scheduled</Badge>;
    }
    if (now > validUntil) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (voucher.maxUses && voucher.currentUses >= voucher.maxUses) {
      return <Badge variant="destructive">Limit Reached</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-xl sm:text-3xl font-bold">Voucher Management</h1>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Voucher
        </Button>
      </div>

      <div className="grid gap-4">
        {vouchers?.map((voucher: any) => (
          <Card key={voucher._id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {voucher.name}
                    {getStatusBadge(voucher)}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Code: <span className="font-mono font-bold">{voucher.code}</span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      setSelectedVoucher(voucher);
                      setShowStatsDialog(true);
                    }}
                  >
                    <BarChart3 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => {
                      setSelectedVoucher(voucher);
                      setShowCreateDialog(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="destructive"
                    disabled={deleteMutation.isPending}
                    onClick={() => {
                      if (confirm(`Delete voucher "${voucher.code}"? This cannot be undone.`)) {
                        deleteMutation.mutate({ id: voucher._id });
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Discount</p>
                  <p className="font-medium">{getDiscountDisplay(voucher)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Usage</p>
                  <p className="font-medium">
                    {voucher.currentUses} / {voucher.maxUses || '∞'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Valid From</p>
                  <p className="font-medium">
                    {new Date(voucher.validFrom).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Valid Until</p>
                  <p className="font-medium">
                    {new Date(voucher.validUntil).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {voucher.description && (
                <p className="text-sm text-muted-foreground mt-4">
                  {voucher.description}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <CreateVoucherDialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setSelectedVoucher(null);
        }}
        voucher={selectedVoucher}
      />

      <VoucherStatsDialog
        open={showStatsDialog}
        onClose={() => {
          setShowStatsDialog(false);
          setSelectedVoucher(null);
        }}
        voucherId={selectedVoucher?._id}
      />
    </div>
  );
}
